import { flexRender, Header, SortDirection } from '@tanstack/react-table';
import { tableStrings } from './i18n/de-de';

import styles from './bmweDataTable.module.css';
import { BmweColumnMeta } from '../../types';
import { CSSProperties, useRef } from 'react';
import { ColumnFilterPopup } from './parts/filterPin';
import filterStyles from './parts/filterPin.module.css';

interface BmweTableHeaderProps<T extends Record<string, unknown>> {
  header: Header<T, unknown>;
  onResizeStart?: (columnId: string, th: HTMLElement) => void;
  onAutoFit?: (columnId: string, th: HTMLElement) => void;
  isLastColumn?: boolean;
}

export function BmweTableHeader<T extends Record<string, unknown>>({
  header,
  onResizeStart,
  onAutoFit,
  isLastColumn,
}: BmweTableHeaderProps<T>) {
  const thRef = useRef<HTMLTableCellElement>(null);
  const meta = header.column.columnDef.meta as BmweColumnMeta | undefined;
  const justify = meta?.justify ?? 'start';

  const sortIcon = (
    sortDirection: SortDirection | boolean,
    canSort: boolean,
  ) => {
    if (!canSort) {
      return null;
    }

    switch (sortDirection) {
      case 'asc':
        return <i className="pi pi-sort-amount-up" aria-hidden="true"></i>;
      case 'desc':
        return <i className="pi pi-sort-amount-down" aria-hidden="true"></i>;
      default:
        return <i className="pi pi-sort-alt" aria-hidden="true"></i>;
    }
  };

  // Determine sort status for screen readers
  const getSortStatus = () => {
    if (!header.column.getCanSort()) return '';

    const sortDir = header.column.getIsSorted();
    if (sortDir === 'asc') return tableStrings.sortStatusAsc;
    if (sortDir === 'desc') return tableStrings.sortStatusDesc;
    return '';
  };

  return (
    <th
      ref={thRef}
      key={header.id}
      colSpan={header.colSpan}
      scope="col"
      data-column-id={header.column.id}
      className={`${styles.bmweDataTableHeader} ${styles.tableHeader} ${styles.tableHeaderFocus}`}
      style={
        {
          '--columnJustify': justify,
        } as CSSProperties
      }
      aria-sort={
        header.column.getIsSorted() === 'asc'
          ? 'ascending'
          : header.column.getIsSorted() === 'desc'
            ? 'descending'
            : undefined
      }
    >
      {header.isPlaceholder ? null : (
        <div className={styles.headerContent}>
          <div
            className={`${styles.firstRow} ${
              header.column.getCanSort() ? styles.buttonFocus : ''
            }`}
            onClick={
              header.column.getCanSort()
                ? header.column.getToggleSortingHandler()
                : undefined
            }
            data-sortable={header.column.getCanSort()}
            role={header.column.getCanSort() ? 'button' : undefined}
            tabIndex={header.column.getCanSort() ? 0 : undefined}
            aria-label={
              header.column.getCanSort()
                ? `${flexRender(
                    header.column.columnDef.header,
                    header.getContext(),
                  )}, ${getSortStatus()}`
                : undefined
            }
            title={
              header.column.getCanSort()
                ? header.column.getNextSortingOrder() === 'asc'
                  ? tableStrings.sortAsc
                  : header.column.getNextSortingOrder() === 'desc'
                    ? tableStrings.sortDesc
                    : tableStrings.noSort
                : undefined
            }
            onKeyDown={(e) => {
              if (
                header.column.getCanSort() &&
                (e.key === 'Enter' || e.key === ' ')
              ) {
                e.preventDefault();
                header.column.toggleSorting();
              }
            }}
          >
            <span className={styles.headerLabel}>
              {flexRender(header.column.columnDef.header, header.getContext())}
            </span>
            <div className={styles.headerActions}>
              {sortIcon(
                header.column.getIsSorted(),
                header.column.getCanSort(),
              )}
              {header.column.getCanFilter() && (
                // Keeps clicks and keys of the filter (and its popup) from sorting.
                <span
                  onClick={(e) => e.stopPropagation()}
                  onKeyDown={(e) => e.stopPropagation()}
                >
                  <HeaderFilter header={header} />
                </span>
              )}
            </div>
          </div>
        </div>
      )}
      {!isLastColumn && onResizeStart && (
        <div
          className={styles.resizeHandle}
          role="separator"
          aria-label="Resize column"
          tabIndex={-1}
          onMouseDown={(e) => {
            e.stopPropagation();
            e.preventDefault();
            if (thRef.current) {
              onResizeStart(header.column.id, thRef.current);
            }
          }}
          onDoubleClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            if (onAutoFit && thRef.current) {
              onAutoFit(header.column.id, thRef.current);
            }
          }}
        />
      )}
    </th>
  );
}

function HeaderFilter<T extends Record<string, unknown>>({
  header,
}: {
  header: Header<T, unknown>;
}) {
  const { column } = header;
  const value = column.getFilterValue();
  const label = tableStrings.messages.filterBy(
    String(column.columnDef.header || column.id),
  );

  return (
    <ColumnFilterPopup
      column={column}
      value={value}
      onApply={(next) => column.setFilterValue(next)}
      renderTrigger={(open) => (
        <button
          type="button"
          className={`${filterStyles.headerFilterButton} ${value !== undefined ? filterStyles.active : ''}`}
          aria-label={label}
          title={label}
          aria-haspopup="dialog"
          aria-expanded={open}
        >
          <i
            className={`pi ${value !== undefined ? 'pi-filter-fill' : 'pi-filter'}`}
            aria-hidden="true"
          />
        </button>
      )}
    />
  );
}

export default BmweTableHeader;
